import React, { useMemo } from 'react';

const findService = (summary, services) => {
  if (!summary || !services.length) return null;
  const normSummary = summary.toLowerCase();
  
  // 1. Try exact/includes
  let found = services.find(s => normSummary.includes(s.nombre.toLowerCase()));
  if (found) return found;
  
  // 2. Try significant words (>3 chars)
  for (const s of services) {
    const words = s.nombre.toLowerCase().split(/[ /+&,.-]+/).filter(w => w.length > 3);
    if (words.length > 0 && words.some(w => normSummary.includes(w))) return s;
  }
  
  // 3. Contextual fallback
  const isGeneric = normSummary.includes('✂️') || 
                    normSummary.includes('peluquería') || 
                    normSummary.includes('corte') || 
                    normSummary.includes('cita');
                    
  if (isGeneric) {
    return services.find(s => s.nombre.toLowerCase().includes('adulto')) || services[0];
  }
  
  return null;
};

const TopServices = ({ appointments = [], services = [] }) => {
  const data = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Count service usage this month
    const usage = {};
    let totalCount = 0;
    
    appointments.forEach(apt => {
      const aptDate = new Date(apt.fecha);
      if (aptDate.getMonth() !== currentMonth || aptDate.getFullYear() !== currentYear) return;
      
      // Filter out blocks
      if (apt.summary.toLowerCase().includes('bloqueado') || 
          apt.summary.toLowerCase().includes('cierre')) return;
      
      const service = findService(apt.summary, services);
      
      if (service) {
        const id = service.id || service._id;
        usage[id] = (usage[id] || 0) + 1;
        totalCount++;
      }
    });

    // Map with full service data
    const topData = services
      .map(service => {
        const id = service.id || service._id;
        const count = usage[id] || 0;
        return {
          ...service,
          count,
          percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0,
          revenue: count * (typeof service.precio === 'string' ? parseFloat(service.precio) : service.precio || 0)
        };
      })
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4); // Top 4

    return { topData, totalCount };
  }, [appointments, services]);

  const { topData: stats, totalCount } = data;

  // Calculate paths for donut chart SVG
  const segments = useMemo(() => {
    let accumulated = 0;
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];
    
    return stats.map((stat, idx) => {
      const startAngle = (accumulated / 100) * Math.PI * 2 - Math.PI / 2;
      accumulated += stat.percentage;
      const endAngle = (accumulated / 100) * Math.PI * 2 - Math.PI / 2;
      
      const x1 = 50 + 35 * Math.cos(startAngle);
      const y1 = 50 + 35 * Math.sin(startAngle);
      const x2 = 50 + 35 * Math.cos(endAngle);
      const y2 = 50 + 35 * Math.sin(endAngle);
      
      const largeArc = stat.percentage > 50 ? 1 : 0;
      
      return {
        ...stat,
        path: `M 50 50 L ${x1} ${y1} A 35 35 0 ${largeArc} 1 ${x2} ${y2} Z`,
        color: colors[idx % colors.length]
      };
    });
  }, [stats]);

  const totalRevenue = stats.reduce((sum, s) => sum + s.revenue, 0);

  if (stats.length === 0) {
    return (
      <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
        <h3 className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider mb-4">
          Servicios más elegidos
        </h3>
        <p className="text-zinc-600 text-sm text-center py-8 italic">Sin datos este mes</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-card)] rounded-xl p-6 border border-[var(--border-main)]" style={{ boxShadow: 'var(--shadow-card)' }}>
      <h3 className="text-[var(--text-muted)] text-[10px] font-bold uppercase tracking-wider mb-4">
        Servicios más elegidos
      </h3>
      
      <div className="flex items-center gap-4">
        {/* Donut Chart */}
        <div className="relative w-24 h-24 flex-shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            {segments.map((seg, idx) => (
              <path 
                key={seg.id || idx} 
                d={seg.path} 
                fill={seg.color} 
                className="hover:opacity-80 transition-opacity cursor-pointer"
              />
            ))}
            <circle cx="50" cy="50" r="22" className="fill-[var(--bg-card)]" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-[var(--text-main)] leading-none">{totalCount}</span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 min-w-0 space-y-2">
          {segments.map((stat) => (
            <div key={stat.id} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span 
                  className="w-2 h-2 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: stat.color }}
                />
                <span className="text-[var(--text-secondary)] truncate font-medium">
                  {stat.nombre}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[var(--text-main)] font-bold">{stat.count}</span>
                <span className="text-[var(--text-muted)] text-[9px]">({stat.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--border-main)] flex justify-between items-center">
        <span className="text-[var(--text-muted)] text-xs">Total generado</span>
        <span className="text-emerald-500 font-bold text-sm">{totalRevenue}€</span>
      </div>
    </div>
  );
};

export default TopServices;
